import React from 'react';
import {
  Building2, Users, MapPin, Settings, BookOpen, MessageSquareQuote, Palette, Share2, Bell, UserCircle,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Spinner, EmptyState } from '../../components/ui';
import QuickLinks from '../../components/QuickLinks';
import DashboardStatus from '../../components/DashboardStatus';
import { useLivePoll } from '../../lib/useLivePoll';

const QUICK_LINKS = [
  { to: '/it/properties', label: 'Properties', icon: <Building2 className="h-5 w-5" />, description: 'Listings & media' },
  { to: '/it/staff', label: 'Staff & Users', icon: <Users className="h-5 w-5" />, description: 'Accounts & roles' },
  { to: '/it/estates', label: 'Estates', icon: <Building2 className="h-5 w-5" />, description: 'Facilities' },
  { to: '/it/locations', label: 'States & Areas', icon: <MapPin className="h-5 w-5" />, description: 'Locations' },
  { to: '/it/features', label: 'Features', icon: <Settings className="h-5 w-5" />, description: 'Property features' },
  { to: '/it/staff-handbook', label: 'Staff Handbook', icon: <BookOpen className="h-5 w-5" />, description: 'Policies & docs' },
  { to: '/it/testimonials', label: 'Testimonials', icon: <MessageSquareQuote className="h-5 w-5" />, description: 'Reviews' },
  { to: '/it/appearance', label: 'Appearance', icon: <Palette className="h-5 w-5" />, description: 'Theme & branding' },
  { to: '/it/contact', label: 'Contact & Socials', icon: <Share2 className="h-5 w-5" />, description: 'Contact settings' },
  { to: '/it/notices', label: 'Notices', icon: <Bell className="h-5 w-5" />, description: 'Announcements' },
  { to: '/it/profile', label: 'My Profile', icon: <UserCircle className="h-5 w-5" />, description: 'Account details' },
];

interface DashboardPayload {
  role: string;
  stats: {
    totalProperties: number;
    availableProperties: number;
    featuredProperties: number;
    staffCount: number;
    managerCount: number;
    accountCount: number;
    oversightCount: number;
    totalUsers: number;
  };
}

const STATS: { key: keyof DashboardPayload['stats']; label: string; icon: React.ReactNode; color: string }[] = [
  { key: 'totalProperties', label: 'Total Properties', icon: <Building2 className="h-6 w-6" />, color: 'bg-primary/10 text-primary' },
  { key: 'availableProperties', label: 'Available Properties', icon: <Building2 className="h-6 w-6" />, color: 'bg-green-50 text-green-600' },
  { key: 'featuredProperties', label: 'Featured Properties', icon: <Building2 className="h-6 w-6" />, color: 'bg-amber-50 text-amber-600' },
  { key: 'totalUsers', label: 'Total Users', icon: <Users className="h-6 w-6" />, color: 'bg-primary/10 text-primary' },
  { key: 'staffCount', label: 'Staff', icon: <Users className="h-6 w-6" />, color: 'bg-purple-50 text-purple-600' },
  { key: 'managerCount', label: 'Managers', icon: <Users className="h-6 w-6" />, color: 'bg-primary/10 text-primary' },
  { key: 'accountCount', label: 'Accounts', icon: <Users className="h-6 w-6" />, color: 'bg-teal-50 text-teal-600' },
  { key: 'oversightCount', label: 'Oversight', icon: <Users className="h-6 w-6" />, color: 'bg-rose-50 text-rose-600' },
];

export default function Dashboard() {
  const { data, loading, refreshing, lastUpdated, reload } = useLivePoll<DashboardPayload>(
    () => api.get<DashboardPayload>('/dashboard')
  );

  if (loading) return <Spinner label="Loading dashboard..." />;
  if (!data) return <EmptyState message="Could not load dashboard data." />;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">Oversight Dashboard</h1>
        <DashboardStatus lastUpdated={lastUpdated} refreshing={refreshing} onRefresh={reload} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {STATS.map((s) => (
          <div key={s.key} className="bg-white rounded-lg shadow-lg p-5">
            <div className={`inline-flex p-2.5 rounded-lg mb-3 ${s.color}`}>{s.icon}</div>
            <p className="text-2xl font-bold">{data.stats[s.key]}</p>
            <p className="text-sm text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      <QuickLinks links={QUICK_LINKS} title="All Information" />
    </div>
  );
}